import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useQueryClient } from "@tanstack/react-query";
import type { UserApi, CreateUserRequestApi } from "@/types/api/users";
import type { User, UserRole, UserStatus, UserPermissions, CreateUserFormData } from "@/types/user";
import { mockUsers } from "@/lib/mock-users";

/**
 * Map API roleId to frontend UserRole
 */
function mapRoleIdToRole(roleId: number): UserRole {
  switch (roleId) {
    case 1:
      return 'administrator';
    case 2:
      return 'user';
    case 3:
      return 'view_only';
    default:
      return 'user';
  }
}

/**
 * Map frontend UserRole to API roleId
 */
function mapRoleToRoleId(role: UserRole): number {
  switch (role) {
    case 'administrator':
      return 1;
    case 'user':
      return 2;
    case 'view_only':
      return 3;
    default:
      return 2;
  }
}

/**
 * Map API statusId to frontend UserStatus
 */
function mapStatusIdToStatus(statusId: number): UserStatus {
  switch (statusId) {
    case 1:
      return 'active';
    case 2:
      return 'inactive';
    default:
      return 'active';
  }
}

/**
 * Map frontend UserStatus to API statusId
 */
function mapStatusToStatusId(status: UserStatus): number {
  switch (status) {
    case 'active':
      return 1;
    case 'inactive':
      return 2;
    default:
      return 1;
  }
}

/**
 * Map API user to frontend User type
 */
function mapUserApiToUser(apiUser: UserApi): User {
  const permissions: UserPermissions = {
    manageUsers: apiUser.canManageUsers,
    manageCompanies: apiUser.canManageCompanies,
    manageIntegrations: apiUser.canManageIntegrations,
    configureAutomation: apiUser.canConfigureAutomation,
    configureAI: apiUser.canConfigureAI,
    viewReports: apiUser.canViewReports,
    manageAccounts: apiUser.canManageAccounts,
    sendCommunications: apiUser.canSendCommunications,
    logActivities: apiUser.canLogActivities,
  };

    return {
      id: String(apiUser.id),
      externalUserId: apiUser.externalUserId ?? undefined,
      externalFirstName: apiUser.externalFirstName ?? undefined,
      externalLastName: apiUser.externalLastName ?? undefined,
      firstName: apiUser.firstName,
      lastName: apiUser.lastName,
    email: apiUser.email,
    phone: apiUser.phone ?? undefined,
    role: mapRoleIdToRole(apiUser.roleId),
    status: mapStatusIdToStatus(apiUser.statusId),
    avatar: apiUser.avatar ?? undefined,
    companies: [], // API doesn't return company associations in the list endpoint
    permissions,
    lastLogin: apiUser.lastLogin ?? undefined,
    createdAt: apiUser.createdAt,
    updatedAt: apiUser.updatedAt ?? undefined,
  };
}

/**
 * Map frontend CreateUserFormData to API CreateUserRequestApi
 */
function mapCreateUserFormToApi(formData: CreateUserFormData): CreateUserRequestApi {
  return {
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    phone: formData.phone ?? null,
    roleId: mapRoleToRoleId(formData.role),
    statusId: 1, // Default to active
    canManageUsers: formData.permissions.manageUsers,
    canManageCompanies: formData.permissions.manageCompanies,
    canManageIntegrations: formData.permissions.manageIntegrations,
    canConfigureAutomation: formData.permissions.configureAutomation,
    canConfigureAI: formData.permissions.configureAI,
    canViewReports: formData.permissions.viewReports,
    canManageAccounts: formData.permissions.manageAccounts,
    canSendCommunications: formData.permissions.sendCommunications,
    canLogActivities: formData.permissions.logActivities,
  };
}

/**
 * Hook to fetch users list data for the Users page
 * Handles both Demo and Live modes using useModeAwareQuery
 */
export function useUsersList() {
  const { apiGet } = useApi();

  return useModeAwareQuery<User[], Error>(
    ["settings-users"],
    async ({ signal }) => {
      const response = await apiGet<UserApi[]>("/api/v1/users", undefined, signal);
      return response.map(mapUserApiToUser);
    },
    mockUsers,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Hook to create a new user
 * In demo mode, returns a mock response
 * In live mode, calls the POST /api/v1/users endpoint
 */
export function useCreateUser() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<User, Error, CreateUserFormData>(
    async (formData) => {
      const requestData = mapCreateUserFormToApi(formData);
      const response = await apiPost<UserApi>("/api/v1/users", requestData);
      return mapUserApiToUser(response);
    },
    (formData) => {
      // Demo mode: create a mock user
      const mockUser: User = {
        id: String(Date.now()),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        status: 'active',
        companies: formData.companies,
        permissions: formData.permissions,
        createdAt: new Date().toISOString(),
      };
      return mockUser;
    },
    {
      onSuccess: () => {
        // Invalidate the users list query to refetch
        queryClient.invalidateQueries({ queryKey: ["settings-users"] });
      },
    }
  );
}

/**
 * Hook to delete a user
 * In demo mode, returns success
 * In live mode, calls the DELETE /api/v1/users/{id} endpoint
 */
export function useDeleteUser() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, string>(
    async (userId) => {
      await apiDelete(`/api/v1/users/${userId}`);
    },
    () => {
      // Demo mode: just return success
      return;
    },
    {
      onSuccess: () => {
        // Invalidate the users list query to refetch
        queryClient.invalidateQueries({ queryKey: ["settings-users"] });
      },
    }
  );
}
